import type { AiMatch, Itinerary } from "../types";
import type { ItinerarySlot, Trip, TripDay, TripStop, TripStopSnapshot, TripStyle } from "../types";
import { generateId } from "./id";

/**
 * When a plan slot starts, from the API's stable slot key (the label is
 * localized only on screen). "Also" and anything unknown stay untimed, so the
 * day's own slot rule places them.
 */
const SLOT_START: Record<string, string> = {
  morning: "09:30",
  breakfast: "09:00",
  "full day": "09:00",
  lunch: "13:00",
  midday: "13:00",
  afternoon: "15:00",
  evening: "19:30",
  dinner: "20:00",
  night: "21:00",
};

/** Item id prefix of a plain stop. */
export const PLAIN_STOP_PREFIX = "plain:";

export function slotStartTime(time: string | undefined): string | undefined {
  return time ? SLOT_START[time.trim().toLowerCase()] : undefined;
}

function cleanNote(note: string | undefined): string | undefined {
  const n = note?.trim();
  return n ? n.slice(0, 280) : undefined;
}

function snapshotOfMatch(m: AiMatch): TripStopSnapshot | undefined {
  const title = m.title?.trim();
  if (!title) return undefined;
  return { title, image: m.heroImageUrl ?? null, kind: m.categoryName ?? m.subtitle ?? null };
}

/**
 * Everything a plan slot becomes in a trip (traveller tests, 2026-11-04).
 *
 * Before, only slots with a listing survived, with no time and no note: a
 * three-day plan of fifteen stops landed as five bare cards. Now a listed
 * place keeps the plan's time and sentence and a snapshot (so it never reads
 * as gone), and a stop we do not list ("Lunch in the medina") comes in as a
 * plain line. A slot that names a listing we cannot find is still dropped.
 */
export function stopFromSlot(slot: ItinerarySlot, bySlug: ReadonlyMap<string, AiMatch>): TripStop | null {
  const startTime = slotStartTime(slot.time);
  const timed = startTime ? { startTime } : {};
  const m = slot.matchSlug ? bySlug.get(slot.matchSlug) : undefined;
  if (m?.id) {
    const note = cleanNote(slot.note);
    const snapshot = snapshotOfMatch(m);
    return {
      id: generateId("stop"),
      itemId: m.id,
      source: { kind: "chat" },
      ...timed,
      ...(note ? { note } : {}),
      ...(snapshot ? { snapshot } : {}),
    };
  }
  const title = slot.place?.title?.trim();
  if (slot.matchSlug || !title) return null;
  const note = cleanNote(slot.place?.note);
  const id = generateId("stop");
  return {
    id,
    // Never empty: account sync requires an item id on every stop. The
    // prefix matches no catalogue row, so every lookup reads it as unlisted.
    itemId: `${PLAIN_STOP_PREFIX}${id}`,
    plain: true,
    source: { kind: "chat" },
    snapshot: { title: title.slice(0, 80) },
    ...timed,
    ...(note ? { note } : {}),
  };
}

/** A plan day's city name ("Marrakech") as a city slug, from the plan's own cards. */
function citySlugOf(name: string | undefined, matches: AiMatch[]): string | undefined {
  if (!name) return undefined;
  const wanted = name.trim().toLowerCase();
  const hit = matches.find((m) => m.cityName?.trim().toLowerCase() === wanted && m.citySlug);
  if (hit?.citySlug) return hit.citySlug;
  const slug = wanted
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || undefined;
}

function daysToTripDays(
  itinerary: Itinerary,
  matches: AiMatch[],
  labelOffset: number,
): TripDay[] {
  const bySlug = new Map(matches.map((m) => [m.slug, m]));
  return itinerary.days.map((day, i) => {
    const stops = day.slots
      .map((slot) => stopFromSlot(slot, bySlug))
      .filter((s): s is TripStop => s !== null);
    const city = citySlugOf(day.city, matches);
    return { id: generateId("day"), label: `Day ${labelOffset + i + 1}`, ...(city ? { city } : {}), stops };
  });
}

export function deriveTripName(itinerary: Itinerary, fallbackName: string): string {
  if (itinerary.cities && itinerary.cities.length > 0) {
    return itinerary.cities.join(" & ");
  }
  const firstCity = itinerary.days.find((d) => d.city)?.city;
  return firstCity ?? fallbackName;
}

function firstCitySlug(itinerary: Itinerary, matches: AiMatch[]): string {
  const fromDay = citySlugOf(itinerary.days.find((d) => d.city)?.city, matches);
  if (fromDay) return fromDay;
  const bySlug = new Map(matches.map((m) => [m.slug, m]));
  for (const day of itinerary.days) {
    for (const slot of day.slots) {
      const m = slot.matchSlug ? bySlug.get(slot.matchSlug) : undefined;
      if (m?.citySlug) return m.citySlug;
    }
  }
  return "";
}

function validIsoDay(v: string | undefined): string | undefined {
  return v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined;
}

/** The party the traveller gave in the chat, when they gave one. */
function partyOf(itinerary: Itinerary): { people?: number; childAges?: number[] } {
  const adults = itinerary.adults;
  if (!adults || adults < 1) return {};
  const kids = (itinerary.childAges ?? []).filter((a) => Number.isInteger(a) && a >= 0 && a <= 17);
  return { people: adults + kids.length, ...(kids.length ? { childAges: kids } : {}) };
}

export function buildTripFromItinerary(
  itinerary: Itinerary,
  matches: AiMatch[],
  fallbackName: string,
  opts?: { name?: string; style?: TripStyle; startDate?: string },
): Trip {
  const startDate = opts?.startDate ?? validIsoDay(itinerary.startDate);
  const party = partyOf(itinerary);
  return {
    id: generateId("trip"),
    name: opts?.name ?? deriveTripName(itinerary, fallbackName),
    city: firstCitySlug(itinerary, matches),
    style: opts?.style ?? "culture",
    createdAt: new Date().toISOString(),
    // The traveller's real first day ("2026-11-06"), when they gave dates.
    // Left out otherwise; trip-storage then dates the trip from createdAt.
    ...(startDate ? { startDate } : {}),
    ...party,
    days: daysToTripDays(itinerary, matches, 0),
  };
}

export function mergeItineraryIntoTrip(
  activeTrip: Trip,
  itinerary: Itinerary,
  matches: AiMatch[],
): Trip {
  const appended = daysToTripDays(itinerary, matches, activeTrip.days.length);
  return { ...activeTrip, days: [...activeTrip.days, ...appended] };
}

/**
 * How many of the trip's own days an AI plan can actually fill.
 *
 * The button says this number, so it has to be the number the fill produces:
 * a 2-day plan on a 3-day trip fills 2, a 5-day plan on a 3-day trip fills 3.
 */
export function fillableDayCount(trip: Trip, itinerary: Itinerary): number {
  return Math.min(trip.days.length, itinerary.days.length);
}

/**
 * Lay an AI plan over the days the traveller already made.
 *
 * `mergeItineraryIntoTrip` appends — right when a chat itinerary arrives with
 * no trip to land in, wrong here. Someone who built a 3-day Marrakech trip and
 * asked the concierge to plan it wants those three days filled, not three empty
 * days followed by the AI's two. Day count never changes; a plan shorter than
 * the trip leaves the rest blank (the empty-day suggestions already speak
 * there) and a plan longer than the trip stops at the last day the traveller
 * asked for.
 *
 * Existing stops are kept and the plan lands after them, so this never eats
 * work already on the day. Anything already somewhere in the trip is skipped —
 * re-adding a place you have is noise, the same rule the vibe suggestions use.
 */
export function fillTripDaysFromItinerary(
  trip: Trip,
  itinerary: Itinerary,
  matches: AiMatch[],
): Trip {
  const bySlug = new Map(matches.map((m) => [m.slug, m]));
  const taken = new Set(trip.days.flatMap((d) => d.stops.map((s) => s.itemId)).filter((id) => !id.startsWith(PLAIN_STOP_PREFIX)));

  const days = trip.days.map((day, i) => {
    const planned = itinerary.days[i];
    if (!planned) return day;

    const added: TripStop[] = [];
    for (const slot of planned.slots) {
      const stop = stopFromSlot(slot, bySlug);
      if (!stop) continue;
      if (!stop.plain) {
        if (taken.has(stop.itemId)) continue;
        taken.add(stop.itemId);
      }
      added.push(stop);
    }
    return added.length === 0 ? day : { ...day, stops: [...day.stops, ...added] };
  });

  return { ...trip, days };
}

export function countStops(trip: Trip): number {
  return trip.days.reduce((n, d) => n + d.stops.length, 0);
}
